// src/pages/UserWallet.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  InputAdornment,
  Avatar,
  Badge,
   FormControl,     
  InputLabel,      
  Select,          
  MenuItem         
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Receipt as ReceiptIcon,
  Church as ChurchIcon,
  VolunteerActivism as GiveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useWallet } from '../contexts/WalletContext';
import { db } from '../firebase/config';
import { doc, updateDoc, increment, collection, addDoc, Timestamp } from 'firebase/firestore';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './UserWallet.css';

const UserWallet = () => {
  const { currentUser, userData } = useAuth();
  const { showToast } = useToast();
  const { 
    wallet, 
    walletBalance, 
    transactions, 
    pledges,
    walletAccounts,
    initiateMpesaPayment,
    addFunds,
    makePayment,
    createPledge,
    payPledge,
    loadWallet,
    loadTransactions,
    loadPledges
  } = useWallet();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [amount, setAmount] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [paymentReason, setPaymentReason] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [pledgeData, setPledgeData] = useState({
    amount: '',
    reason: '',
    description: '',
    dueDate: ''
  });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [lastPaymentId, setLastPaymentId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Available payment reasons for spending
  const paymentReasons = [
    { value: 'missions', label: 'Missions Support', icon: '🌍' },
    { value: 'social_lunch', label: 'Social Lunch', icon: '🍽️' },
    { value: 'sabbath_fare', label: 'Sabbath Outing Fare', icon: '🚌' },
    { value: 'contributions', label: 'General Contributions', icon: '💝' },
    { value: 'development', label: 'Development Fund', icon: '🏗️' },
    { value: 'pledge', label: 'Pledge Payment', icon: '📝' }
  ];

  // Reload data on mount
  useEffect(() => {
    loadWallet();
    loadTransactions();
    loadPledges();
  }, []);

  // Auto-check payment status every 5 seconds if there's a pending payment
  useEffect(() => {
    let interval;
    if (lastPaymentId) {
      interval = setInterval(() => {
        checkPaymentStatus(lastPaymentId);
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lastPaymentId]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'KES 0';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16 }} />;
      case 'pending': return <PendingIcon sx={{ color: '#ff9800', fontSize: 16 }} />;
      case 'failed': return <CancelIcon sx={{ color: '#f44336', fontSize: 16 }} />;
      default: return null;
    }
  };

  const handleDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      showSnackbar('Please enter a valid amount', 'error');
      return;
    }

    if (paymentMethod === 'mpesa') {
      if (!phoneNumber) {
        showSnackbar('Please enter M-Pesa phone number', 'error');
        return;
      }
      
      setLoading(true);
      const result = await initiateMpesaPayment(
        phoneNumber,
        parseFloat(amount),
        selectedAccount?.id || 'main',
        selectedAccount?.name || 'Main Wallet',
        `Deposit to ${selectedAccount?.name || 'Main Wallet'}`
      );
      
      if (result.success) {
        setLastPaymentId(result.checkoutRequestID);
        showSnackbar('STK Push sent! Please check your phone and enter PIN.', 'info');
        setShowDepositModal(false);
        setAmount('');
        setPhoneNumber('');
      }
      setLoading(false);
    } else {
      // Manual deposit (admin only in real scenario, but for demo)
      setLoading(true);
      const result = await addFunds(
        parseFloat(amount),
        selectedAccount?.id || 'main',
        selectedAccount?.name || 'Main Wallet',
        `Manual deposit to ${selectedAccount?.name || 'Main Wallet'}`,
        'manual'
      );
      if (result.success) {
        setShowDepositModal(false);
        setAmount('');
      }
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      showSnackbar('Please enter a valid amount', 'error');
      return;
    }

    if (!paymentReason) {
      showSnackbar('Please select a payment reason', 'error');
      return;
    }

    if (parseFloat(amount) > walletBalance) {
      showSnackbar('Insufficient wallet balance', 'error');
      return;
    }

    setLoading(true);
    const result = await makePayment(
      parseFloat(amount),
      paymentReason,
      paymentReasons.find(r => r.value === paymentReason)?.label,
      paymentDescription || `Payment for ${paymentReasons.find(r => r.value === paymentReason)?.label}`,
      paymentReason
    );

    if (result.success) {
      setShowPaymentModal(false);
      setAmount('');
      setPaymentReason('');
      setPaymentDescription('');
      showSnackbar('Payment completed successfully!', 'success');
    }
    setLoading(false);
  };

  const handleCreatePledge = async () => {
    if (!pledgeData.amount || parseFloat(pledgeData.amount) <= 0) {
      showSnackbar('Please enter a valid pledge amount', 'error');
      return;
    }
    if (!pledgeData.reason) {
      showSnackbar('Please select a reason for your pledge', 'error');
      return;
    }

    setLoading(true);
    const result = await createPledge(
      parseFloat(pledgeData.amount),
      pledgeData.reason,
      pledgeData.description,
      pledgeData.dueDate || null
    );

    if (result.success) {
      setShowPledgeModal(false);
      setPledgeData({ amount: '', reason: '', description: '', dueDate: '' });
      showSnackbar('Pledge created successfully!', 'success');
    }
    setLoading(false);
  };

  const handlePayPledge = async (pledgeId, amount, pledgeReason) => {
    if (amount > walletBalance) {
      showSnackbar('Insufficient wallet balance', 'error');
      return;
    }

    setLoading(true);
    const result = await payPledge(pledgeId, amount);
    if (result.success) {
      // Also make payment from wallet
      await makePayment(amount, pledgeReason, 'Pledge Payment', `Pledge payment of ${formatCurrency(amount)}`, 'pledge');
      showSnackbar('Pledge payment successful!', 'success');
    }
    setLoading(false);
  };

  const checkPaymentStatus = async (checkoutRequestID) => {
    if (!checkoutRequestID) return;
    
    setCheckingStatus(true);
    try {
      const response = await fetch('/.netlify/functions/mpesa-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkoutRequestID })
      });
      const result = await response.json();
      
      if (result.success && result.status === 'completed') {
        setLastPaymentId(null);
        await loadWallet();
        await loadTransactions();
        showSnackbar('Payment completed successfully!', 'success');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const exportToPDF = async () => {
    const pdf = new jsPDF();
    
    // Header
    pdf.setFillColor(16, 52, 166);
    pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), 40, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.text('WALLET TRANSACTION HISTORY', pdf.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
    
    // User Info
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.text(`Name: ${userData?.fullName || currentUser?.email || 'User'}`, 20, 60);
    pdf.text(`Email: ${currentUser?.email || 'N/A'}`, 20, 70);
    pdf.text(`Current Balance: ${formatCurrency(walletBalance)}`, 20, 80);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 90);
    
    // Account Balances
    let yPos = 110;
    pdf.setFontSize(14);
    pdf.setTextColor(16, 52, 166);
    pdf.text('Account Balances', 20, yPos);
    yPos += 10;
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    walletAccounts.forEach(account => {
      const balance = wallet?.accounts?.[account.id] || 0;
      if (balance > 0 || true) {
        pdf.text(`${account.name}: ${formatCurrency(balance)}`, 25, yPos);
        yPos += 7;
      }
    });
    yPos += 10;
    
    // Transactions Table
    pdf.setFontSize(14);
    pdf.setTextColor(16, 52, 166);
    pdf.text('Transaction History', 20, yPos);
    yPos += 10;
    
    const tableData = transactions.map(t => [
      formatDate(t.createdAt),
      t.type === 'deposit' ? 'Deposit' : 'Spending',
      t.accountName,
      t.description,
      t.type === 'deposit' ? formatCurrency(t.amount) : '-',
      t.type === 'spending' ? formatCurrency(t.amount) : '-'
    ]);
    
    pdf.autoTable({
      startY: yPos,
      head: [['Date', 'Type', 'Account', 'Description', 'Credit', 'Debit']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [16, 52, 166], textColor: 255 },
      foot: [[
        '', '', '', 'TOTAL',
        formatCurrency(transactions.filter(t => t.type === 'deposit').reduce((s, t) => s + t.amount, 0)),
        formatCurrency(transactions.filter(t => t.type === 'spending').reduce((s, t) => s + t.amount, 0))
      ]],
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' }
    });
    
    pdf.save(`wallet_history_${Date.now()}.pdf`);
    showSnackbar('PDF exported successfully!', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Calculate total from each account
  const getAccountTotal = (accountId) => {
    return wallet?.accounts?.[accountId] || 0;
  };

  if (!wallet) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="user-wallet-container" sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box className="wallet-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1034A6', fontWeight: 'bold' }}>
          <WalletIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          My Wallet
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportToPDF}
          sx={{ borderRadius: 2 }}
        >
          Export History
        </Button>
      </Box>

      {/* Main Balance Card */}
      <Card className="balance-card" sx={{ mb: 3, background: 'linear-gradient(135deg, #1034A6, #1A5DE3)', color: 'white' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>Total Balance</Typography>
          <Typography variant="h2" sx={{ fontWeight: 'bold', my: 1 }}>{formatCurrency(walletBalance)}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            <WalletIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Available for ministry activities
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedAccount({ id: 'main', name: 'Main Wallet' });
                setShowDepositModal(true);
              }}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              Deposit Funds
            </Button>
            <Button
              variant="outlined"
              startIcon={<PaymentIcon />}
              onClick={() => setShowPaymentModal(true)}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Make Payment
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Account Balances Grid */}
      <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>Account Balances</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {walletAccounts.map(account => {
          const balance = getAccountTotal(account.id);
          if (balance > 0 || true) { // Show all accounts
            return (
              <Grid item xs={12} sm={6} md={4} key={account.id}>
                <Card sx={{ cursor: 'pointer', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">{account.name}</Typography>
                      <Chip 
                        size="small" 
                        label={balance > 0 ? 'Active' : 'Zero'} 
                        color={balance > 0 ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: '#1034A6' }}>
                      {formatCurrency(balance)}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setSelectedAccount(account);
                          setShowDepositModal(true);
                        }}
                      >
                        Add
                      </Button>
                      <Button
                        size="small"
                        startIcon={<PaymentIcon />}
                        onClick={() => {
                          setPaymentReason(account.id);
                          setShowPaymentModal(true);
                        }}
                      >
                        Pay
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          }
          return null;
        })}
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<HistoryIcon />} label="Transaction History" />
        <Tab icon={<GiveIcon />} label="My Pledges" />
        <Tab icon={<ReceiptIcon />} label="Quick Stats" />
      </Tabs>

      {/* Transaction History Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Transaction History</Typography>
              <IconButton onClick={() => { loadTransactions(); showSnackbar('Refreshed!', 'success'); }} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
            <Divider />
            
            {transactions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ReceiptIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography color="textSecondary">No transactions yet</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowDepositModal(true)}
                  sx={{ mt: 2 }}
                >
                  Make your first deposit
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={transaction.type === 'deposit' ? 'Deposit' : 'Payment'}
                            color={transaction.type === 'deposit' ? 'success' : 'warning'}
                            icon={transaction.type === 'deposit' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                          />
                        </TableCell>
                        <TableCell>{transaction.accountName}</TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 500 }}>
                          {transaction.type === 'deposit' ? formatCurrency(transaction.amount) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f44336', fontWeight: 500 }}>
                          {transaction.type === 'spending' ? formatCurrency(transaction.amount) : '-'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getStatusIcon(transaction.status)}
                            <Typography variant="caption">
                              {transaction.status === 'completed' ? 'Completed' : transaction.status === 'pending' ? 'Pending' : 'Failed'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pledges Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">My Pledges</Typography>
              <Button
                variant="contained"
                startIcon={<GiveIcon />}
                onClick={() => setShowPledgeModal(true)}
                size="small"
              >
                Make a Pledge
              </Button>
            </Box>
            <Divider />
            
            {pledges.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <GiveIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography color="textSecondary">No pledges yet</Typography>
                <Button
                  variant="outlined"
                  startIcon={<GiveIcon />}
                  onClick={() => setShowPledgeModal(true)}
                  sx={{ mt: 2 }}
                >
                  Make your first pledge
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {pledges.map((pledge) => {
                  const progress = (pledge.paidAmount / pledge.amount) * 100;
                  const isCompleted = pledge.status === 'completed';
                  
                  return (
                    <Grid item xs={12} md={6} key={pledge.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {pledge.reason?.replace('_', ' ').toUpperCase()}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Created: {formatDate(pledge.createdAt)}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={pledge.status === 'completed' ? 'Completed' : pledge.status === 'partial' ? 'Partial' : 'Pending'}
                              color={pledge.status === 'completed' ? 'success' : pledge.status === 'partial' ? 'warning' : 'error'}
                            />
                          </Box>
                          
                          <Typography variant="h5" sx={{ color: '#1034A6', mb: 1 }}>
                            {formatCurrency(pledge.amount)}
                          </Typography>
                          
                          <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption">Progress</Typography>
                              <Typography variant="caption">{Math.round(progress)}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                          </Box>
                          
                          <Typography variant="body2" color="textSecondary">
                            Paid: {formatCurrency(pledge.paidAmount)} of {formatCurrency(pledge.amount)}
                          </Typography>
                          {pledge.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>{pledge.description}</Typography>
                          )}
                          {pledge.dueDate && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                              Due: {formatDate(pledge.dueDate)}
                            </Typography>
                          )}
                          
                          {!isCompleted && (
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const payAmount = prompt(`Enter amount to pay (Remaining: ${formatCurrency(pledge.remainingAmount)})`, pledge.remainingAmount);
                                if (payAmount && parseFloat(payAmount) > 0) {
                                  handlePayPledge(pledge.id, parseFloat(payAmount), pledge.reason);
                                }
                              }}
                              sx={{ mt: 2 }}
                              disabled={pledge.remainingAmount <= 0}
                            >
                              Make Payment
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Total Deposits</Typography>
                    <Typography sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                      {formatCurrency(transactions.filter(t => t.type === 'deposit').reduce((s, t) => s + t.amount, 0))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Total Payments</Typography>
                    <Typography sx={{ color: '#f44336', fontWeight: 'bold' }}>
                      {formatCurrency(transactions.filter(t => t.type === 'spending').reduce((s, t) => s + t.amount, 0))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Current Balance</Typography>
                    <Typography sx={{ color: '#1034A6', fontWeight: 'bold', fontSize: 18 }}>
                      {formatCurrency(walletBalance)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Total Transactions</Typography>
                    <Typography fontWeight="bold">{transactions.length}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Active Pledges</Typography>
                    <Typography fontWeight="bold">{pledges.filter(p => p.status !== 'completed').length}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                <Divider sx={{ mb: 2 }} />
                {transactions.slice(0, 5).length === 0 ? (
                  <Typography color="textSecondary" align="center">No recent activity</Typography>
                ) : (
                  <Stack spacing={1}>
                    {transactions.slice(0, 5).map((t) => (
                      <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        {t.type === 'deposit' ? <TrendingUpIcon sx={{ color: '#4caf50' }} /> : <TrendingDownIcon sx={{ color: '#f44336' }} />}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="500">{t.description}</Typography>
                          <Typography variant="caption" color="textSecondary">{formatDate(t.createdAt)}</Typography>
                        </Box>
                        <Typography fontWeight="bold" sx={{ color: t.type === 'deposit' ? '#4caf50' : '#f44336' }}>
                          {t.type === 'deposit' ? '+' : '-'}{formatCurrency(t.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Deposit Modal */}
      <Dialog open={showDepositModal} onClose={() => setShowDepositModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            Deposit to {selectedAccount?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="number"
              label="Amount (KES)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              InputProps={{
                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentMethod}
                label="Payment Method"
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <MenuItem value="mpesa">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon /> M-Pesa (STK Push)
                  </Box>
                </MenuItem>
                <MenuItem value="manual">Manual (Bank/Cash)</MenuItem>
              </Select>
            </FormControl>
            
            {paymentMethod === 'mpesa' && (
              <TextField
                fullWidth
                label="M-Pesa Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="07XX XXX XXX"
                helperText="You will receive a prompt on your phone"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+254</InputAdornment>,
                }}
              />
            )}
            
            {checkingStatus && (
              <Alert severity="info" icon={<PendingIcon />}>
                Processing payment... Please check your phone.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDepositModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeposit}
            disabled={loading || !amount || parseFloat(amount) <= 0 || (paymentMethod === 'mpesa' && !phoneNumber)}
          >
            {loading ? <CircularProgress size={24} /> : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onClose={() => setShowPaymentModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" />
            Make a Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Available Balance: {formatCurrency(walletBalance)}
            </Alert>
            
            <FormControl fullWidth>
              <InputLabel>Payment For</InputLabel>
              <Select
                value={paymentReason}
                label="Payment For"
                onChange={(e) => setPaymentReason(e.target.value)}
              >
                {paymentReasons.map(reason => (
                  <MenuItem key={reason.value} value={reason.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{reason.icon}</span> {reason.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              type="number"
              label="Amount (KES)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              InputProps={{
                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
              }}
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={paymentDescription}
              onChange={(e) => setPaymentDescription(e.target.value)}
              placeholder="Brief description of payment"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePayment}
            disabled={loading || !amount || parseFloat(amount) <= 0 || !paymentReason || parseFloat(amount) > walletBalance}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pledge Modal */}
      <Dialog open={showPledgeModal} onClose={() => setShowPledgeModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GiveIcon color="primary" />
            Make a Pledge
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="number"
              label="Pledge Amount (KES)"
              value={pledgeData.amount}
              onChange={(e) => setPledgeData({ ...pledgeData, amount: e.target.value })}
              placeholder="Enter amount"
              InputProps={{
                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Purpose</InputLabel>
              <Select
                value={pledgeData.reason}
                label="Purpose"
                onChange={(e) => setPledgeData({ ...pledgeData, reason: e.target.value })}
              >
                {paymentReasons.map(reason => (
                  <MenuItem key={reason.value} value={reason.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{reason.icon}</span> {reason.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Description"
              value={pledgeData.description}
              onChange={(e) => setPledgeData({ ...pledgeData, description: e.target.value })}
              placeholder="Additional notes (optional)"
              multiline
              rows={2}
            />
            
            <TextField
              fullWidth
              type="date"
              label="Due Date (Optional)"
              value={pledgeData.dueDate}
              onChange={(e) => setPledgeData({ ...pledgeData, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPledgeModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreatePledge}
            disabled={loading || !pledgeData.amount || parseFloat(pledgeData.amount) <= 0 || !pledgeData.reason}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Pledge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserWallet;
